import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Step, StepConnector, stepConnectorClasses, StepLabel, Stepper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/system';
import clsx from 'clsx';
import { EstateInputDataFormatCategory } from 'queries';

type StepIconProps = {
  active: boolean;
};

const StepIcon = styled('div')<StepIconProps>(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.grey[400],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: active ? theme.palette.primary.light : 'transparent',
  border: `2px solid ${active ? theme.palette.primary.main : theme.palette.grey[400]}`,
  marginBottom: active ? '-2px' : '0',
  margin: 0,
  padding: 0,
}));

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
    left: 'calc(-50% + 12px)',
    right: 'calc(50% + 12px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.grey[400],
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const useStyles = makeStyles((theme) => ({
  labelContainer: {
    fontSize: '12px',
    textAlign: 'center',
    textTransform: 'capitalize',
    cursor: 'pointer',
  },
  stepLabel: {
    borderColor: theme.palette.primary.main,
    cursor: 'pointer',
  },
  stepIcon: {
    borderColor: theme.palette.primary.main,
  },
  activeLabel: {
    fontWeight: 700,
  },
  container: {
    width: '100%',
  },
}));

type Props = {
  categories: EstateInputDataFormatCategory[];
  currentCategory: EstateInputDataFormatCategory;
  onChangeCategory: (category: EstateInputDataFormatCategory) => void;
  completedCategories: EstateInputDataFormatCategory[];
};

const TemplateDataWizardProgressBar = ({
  categories,
  currentCategory,
  onChangeCategory,
  completedCategories,
}: Props) => {
  const classes = useStyles();
  const activeStep = categories.indexOf(currentCategory);

  const handleCategoryClick = (category: EstateInputDataFormatCategory) => {
    if (category !== currentCategory) {
      onChangeCategory(category);
    }
  };

  return (
    <Box className={classes.container}>
      <Stepper alternativeLabel activeStep={activeStep} connector={<CustomConnector />}>
        {categories.map((category, index) => (
          <Step
            key={category}
            completed={index < activeStep}
            onClick={() => handleCategoryClick(category)}
          >
            <StepLabel
              StepIconComponent={(props) => (
                <StepIcon active={props.active} className={classes.stepLabel}>
                  {completedCategories.includes(category) ? (
                    <CheckCircleIcon color="primary" className={classes.stepLabel} />
                  ) : null}
                </StepIcon>
              )}
            >
              <div
                className={clsx(classes.labelContainer, {
                  [classes.activeLabel]: category === currentCategory,
                })}
              >
                {category.replace('_', ' ').toLowerCase()}
              </div>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default TemplateDataWizardProgressBar;
